import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Loader2, Upload, Save, DollarSign, TrendingUp,
  CheckCircle2, Clock3, Info, Pencil, X, Sparkles, MailOpen,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import AdminLayout from '@/components/admin/AdminLayout';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Partner Revenue tracker — admin-only.
 *
 * Closes the visibility gap created by 302 redirects to partner sites:
 * once a click leaves theailedger.ai, only the partner's own affiliate
 * dashboard knows whether the visitor converted. This page is where the
 * admin records each partner's monthly revenue (manual entry OR drop a
 * CSV), joined with that month's click count to compute CVR and RPC at
 * a glance — answering "which partnerships pay" instead of just "which
 * partnerships click".
 */
const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending', icon: Clock3, tone: 'text-yellow-300 border-yellow-500/30 bg-yellow-500/10' },
  { value: 'paid', label: 'Paid (in transit)', icon: DollarSign, tone: 'text-[#00F0FF] border-[#00F0FF]/30 bg-[#00F0FF]/10' },
  { value: 'received', label: 'Received', icon: CheckCircle2, tone: 'text-[#00FF94] border-[#00FF94]/30 bg-[#00FF94]/10' },
];

const MONTH_NAMES = [
  '', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const fmtUsd = (n) =>
  n == null
    ? '—'
    : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(n));

export default function AdminPartnerRevenue() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [data, setData] = useState({ rows: [], totals: null });
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingSlug, setSavingSlug] = useState(null);
  const [editForm, setEditForm] = useState({}); // {slug: {revenue, conversions, status, notes}}
  const [parseModalOpen, setParseModalOpen] = useState(false);
  const csvInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tableRes, summaryRes] = await Promise.all([
        axios.get(
          `${API_URL}/api/admin/partner-revenue?year=${year}&month=${month}`,
          { withCredentials: true },
        ),
        axios.get(
          `${API_URL}/api/admin/partner-revenue/summary?year=${year}`,
          { withCredentials: true },
        ),
      ]);
      setData(tableRes.data);
      setSummary(summaryRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load partner revenue');
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (row) => {
    setEditForm({
      [row.partner_slug]: {
        revenue_usd: row.revenue_usd ?? 0,
        conversions: row.conversions ?? 0,
        status: row.status || 'pending',
        notes: row.notes || '',
      },
    });
  };

  const cancelEdit = () => setEditForm({});

  const saveRow = async (slug) => {
    const f = editForm[slug];
    if (!f) return;
    setSavingSlug(slug);
    try {
      await axios.post(
        `${API_URL}/api/admin/partner-revenue`,
        {
          partner_slug: slug,
          period_year: year,
          period_month: month,
          revenue_usd: Number(f.revenue_usd) || 0,
          conversions: Number(f.conversions) || 0,
          status: f.status,
          notes: (f.notes || '').trim() || null,
        },
        { withCredentials: true },
      );
      toast.success('Saved');
      setEditForm({});
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSavingSlug(null);
    }
  };

  const onCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/partner-revenue/csv`,
        fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      const { inserted, updated, skipped } = res.data;
      toast.success(
        `Imported: ${inserted} new, ${updated} updated` +
        (skipped?.length ? `, ${skipped.length} skipped` : '')
      );
      if (skipped?.length) {
        // Surface the first 3 skipped rows so the admin can fix the CSV.
        skipped.slice(0, 3).forEach((s) => toast.error(`Row ${s.row}: ${s.reason}`));
      }
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'CSV upload failed');
    } finally {
      if (csvInputRef.current) csvInputRef.current.value = '';
    }
  };

  // Year picker covers a sensible window: 2 years back through next year.
  const yearOptions = useMemo(() => {
    const ny = today.getFullYear();
    return [ny - 2, ny - 1, ny, ny + 1];
  }, [today]);

  /**
   * Called by ParseEmailModal once the LLM returns a parsed result the
   * admin has confirmed. Switches the page to the parsed period (so the
   * matching row is on screen) and opens the inline edit form pre-filled
   * with the LLM values — admin still clicks Save, no auto-write.
   */
  const applyParsedCommission = (parsed) => {
    if (!parsed?.partner_slug) {
      toast.error('No partner matched. Pick the row and edit it manually.');
      return;
    }
    setYear(parsed.period_year);
    setMonth(parsed.period_month);
    // Defer the edit-form open by one tick so `load()` has time to refresh
    // when `year`/`month` change. We also stash the pre-fill in editForm so
    // it survives the load.
    setEditForm({
      [parsed.partner_slug]: {
        revenue_usd: parsed.revenue_usd || 0,
        conversions: parsed.conversions || 1,
        status: 'received',
        notes: `Parsed from email · ${parsed.partner_name_in_email || parsed.partner_slug}`,
      },
    });
    setParseModalOpen(false);
    toast.success(`Pre-filled ${parsed.partner_slug} — review and click Save`);
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-partner-revenue-page">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-black mb-1">Partner Revenue</h1>
            <p className="text-neutral-400 text-sm max-w-2xl">
              Record monthly revenue from each affiliate partner (manual or CSV upload from
              Dub, PartnerStack, Rewardful, etc.). Click counts are pulled live for the same
              month so you can see CVR and revenue-per-click side-by-side.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="bg-neutral-900 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#00F0FF]"
              data-testid="partner-revenue-year-select"
            >
              {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="bg-neutral-900 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#00F0FF]"
              data-testid="partner-revenue-month-select"
            >
              {MONTH_NAMES.slice(1).map((name, idx) => (
                <option key={idx + 1} value={idx + 1}>{name}</option>
              ))}
            </select>
            <input
              ref={csvInputRef}
              type="file"
              accept=".csv"
              hidden
              onChange={onCsv}
              data-testid="partner-revenue-csv-input"
            />
            <Button
              onClick={() => setParseModalOpen(true)}
              className="bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-black font-semibold"
              data-testid="partner-revenue-parse-email-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Parse commission email
            </Button>
            <Button
              onClick={() => csvInputRef.current?.click()}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
              data-testid="partner-revenue-csv-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload CSV
            </Button>
          </div>
        </div>

        {parseModalOpen && (
          <ParseEmailModal
            onClose={() => setParseModalOpen(false)}
            onApply={applyParsedCommission}
          />
        )}

        {/* CSV format hint */}
        <div className="rounded-xl border border-[#00F0FF]/20 bg-[#00F0FF]/5 p-4 flex gap-3">
          <Info className="w-5 h-5 text-[#00F0FF] flex-shrink-0 mt-0.5" />
          <div className="text-sm text-neutral-300 leading-relaxed min-w-0 break-words">
            <strong className="text-[#00F0FF]">CSV format:</strong>{' '}
            <code className="text-xs text-[#00F0FF] break-all">
              partner_slug, period_year, period_month, revenue_usd, conversions, status, notes
            </code>
            <br />
            Status values: <code className="text-xs">pending</code>{' / '}
            <code className="text-xs">paid</code>{' / '}
            <code className="text-xs">received</code>. Re-uploading the same partner+month
            replaces the previous numbers — safe to fix typos and re-import.
          </div>
        </div>

        {/* Summary tiles */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <SummaryTile
            label={`${MONTH_NAMES[month]} ${year} revenue`}
            value={fmtUsd(data.totals?.revenue_usd ?? 0)}
            sub={`${data.totals?.conversions ?? 0} conversions · ${data.totals?.clicks ?? 0} clicks`}
            accent="cyan"
            testId="tile-month-revenue"
          />
          <SummaryTile
            label={`${year} year-to-date revenue`}
            value={fmtUsd(summary?.total_revenue_usd ?? 0)}
            sub={`${summary?.total_conversions ?? 0} conversions across all partners`}
            accent="green"
            testId="tile-ytd-revenue"
          />
          <SummaryTile
            label={`Top earner ${year}`}
            value={summary?.top_earner ? summary.top_earner.partner_slug : '—'}
            sub={
              summary?.top_earner
                ? `${fmtUsd(summary.top_earner.revenue_usd)} · ${summary.top_earner.conversions} conv.`
                : 'No revenue logged yet'
            }
            accent="cyan"
            testId="tile-top-earner"
          />
        </div>

        {/* Table */}
        <div className="rounded-2xl border border-white/10 bg-neutral-900/40 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="partner-revenue-table">
              <thead className="bg-black/40 text-neutral-400 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Partner</th>
                  <th className="text-left px-4 py-3 font-semibold">Model</th>
                  <th className="text-right px-4 py-3 font-semibold">Clicks</th>
                  <th className="text-right px-4 py-3 font-semibold">Conv.</th>
                  <th className="text-right px-4 py-3 font-semibold">Revenue</th>
                  <th className="text-right px-4 py-3 font-semibold">CVR</th>
                  <th className="text-right px-4 py-3 font-semibold">RPC</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={9} className="text-center py-12">
                      <Loader2 className="w-6 h-6 animate-spin text-[#00F0FF] mx-auto" />
                    </td>
                  </tr>
                ) : data.rows.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="text-center py-12 text-neutral-500">
                      No partners yet — add one in <code className="text-[#00F0FF]">Affiliate Partners</code>.
                    </td>
                  </tr>
                ) : data.rows.map((row) => {
                  const isEditing = !!editForm[row.partner_slug];
                  const f = editForm[row.partner_slug] || {};
                  return (
                    <tr
                      key={row.partner_slug}
                      className="border-t border-white/5 hover:bg-white/[0.02] transition-colors"
                      data-testid={`partner-revenue-row-${row.partner_slug}`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {row.logo_url && (
                            <div className="w-8 h-8 rounded-lg bg-black/40 border border-white/10 flex items-center justify-center overflow-hidden p-1 flex-shrink-0">
                              <img src={row.logo_url} alt={row.name} className="w-full h-full object-contain" />
                            </div>
                          )}
                          <div>
                            <div className="font-semibold text-white">{row.name}</div>
                            <div className="text-xs text-neutral-500">{row.partner_slug}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-neutral-400">
                        {(row.monetization_model || 'public_affiliate').replace(/_/g, ' ')}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-neutral-300">
                        {row.clicks}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {isEditing ? (
                          <Input
                            type="number"
                            min="0"
                            value={f.conversions}
                            onChange={(e) => setEditForm({
                              ...editForm,
                              [row.partner_slug]: { ...f, conversions: e.target.value },
                            })}
                            className="w-20 text-right h-8 bg-black/40"
                            data-testid={`edit-conversions-${row.partner_slug}`}
                          />
                        ) : (row.conversions || 0)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums font-semibold">
                        {isEditing ? (
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={f.revenue_usd}
                            onChange={(e) => setEditForm({
                              ...editForm,
                              [row.partner_slug]: { ...f, revenue_usd: e.target.value },
                            })}
                            className="w-28 text-right h-8 bg-black/40"
                            data-testid={`edit-revenue-${row.partner_slug}`}
                          />
                        ) : (
                          <span className={row.revenue_usd > 0 ? 'text-[#00FF94]' : 'text-neutral-600'}>
                            {fmtUsd(row.revenue_usd)}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-neutral-400">
                        {row.cvr_pct ? `${row.cvr_pct}%` : '—'}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-neutral-400">
                        {row.rpc_usd ? fmtUsd(row.rpc_usd) : '—'}
                      </td>
                      <td className="px-4 py-3">
                        {isEditing ? (
                          <select
                            value={f.status}
                            onChange={(e) => setEditForm({
                              ...editForm,
                              [row.partner_slug]: { ...f, status: e.target.value },
                            })}
                            className="bg-black/40 border border-white/10 rounded px-2 py-1 text-xs"
                            data-testid={`edit-status-${row.partner_slug}`}
                          >
                            {STATUS_OPTIONS.map((s) => (
                              <option key={s.value} value={s.value}>{s.label}</option>
                            ))}
                          </select>
                        ) : (
                          <StatusPill status={row.status} />
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {isEditing ? (
                          <div className="flex items-center gap-1 justify-end">
                            <Button
                              size="sm"
                              onClick={() => saveRow(row.partner_slug)}
                              disabled={savingSlug === row.partner_slug}
                              className="bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-black h-8"
                              data-testid={`save-row-${row.partner_slug}`}
                            >
                              {savingSlug === row.partner_slug
                                ? <Loader2 className="w-3 h-3 animate-spin" />
                                : <Save className="w-3 h-3" />}
                            </Button>
                            <Button
                              size="sm"
                              onClick={cancelEdit}
                              variant="outline"
                              className="border-white/20 hover:bg-white/10 h-8"
                              data-testid={`cancel-row-${row.partner_slug}`}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => startEdit(row)}
                            variant="ghost"
                            className="text-neutral-400 hover:text-[#00F0FF] hover:bg-[#00F0FF]/10 h-8"
                            data-testid={`edit-row-${row.partner_slug}`}
                          >
                            <Pencil className="w-3 h-3 mr-1" />
                            Edit
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {!loading && data.rows.length > 0 && (
                <tfoot className="bg-black/30">
                  <tr className="border-t border-white/10">
                    <td className="px-4 py-3 font-semibold text-neutral-400 uppercase text-xs tracking-wider">
                      {MONTH_NAMES[month]} {year} totals
                    </td>
                    <td />
                    <td className="px-4 py-3 text-right tabular-nums">{data.totals?.clicks ?? 0}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{data.totals?.conversions ?? 0}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-bold text-[#00FF94]">
                      {fmtUsd(data.totals?.revenue_usd ?? 0)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-neutral-400">
                      {data.totals?.cvr_pct ? `${data.totals.cvr_pct}%` : '—'}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-neutral-400">
                      {data.totals?.rpc_usd ? fmtUsd(data.totals.rpc_usd) : '—'}
                    </td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

function SummaryTile({ label, value, sub, accent, testId }) {
  const accentClass = accent === 'green' ? 'text-[#00FF94]' : 'text-[#00F0FF]';
  return (
    <div
      className="rounded-2xl border border-white/10 bg-neutral-900/40 p-5"
      data-testid={testId}
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-neutral-500 mb-2">
        <TrendingUp className={`w-3.5 h-3.5 ${accentClass}`} />
        {label}
      </div>
      <div className={`text-2xl font-black ${accentClass} tabular-nums`}>{value}</div>
      <div className="text-xs text-neutral-500 mt-1">{sub}</div>
    </div>
  );
}

function StatusPill({ status }) {
  if (!status) return <span className="text-xs text-neutral-600">—</span>;
  const cfg = STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border ${cfg.tone}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

// Field wrapper kept for API symmetry with other admin pages — unused
// here but exported in case future enhancements pull it in.
export function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-neutral-400 mb-1.5 block">
        {label} {required && <span className="text-[#00F0FF]">*</span>}
      </Label>
      {children}
    </div>
  );
}

/**
 * ParseEmailModal — paste raw commission notification email, hit Parse,
 * see the LLM's extraction preview, click "Use these values" to pre-fill
 * the row's edit form. The admin still clicks Save to commit so any
 * misparse is caught by human review (the LLM is good but not perfect).
 */
function ParseEmailModal({ onClose, onApply }) {
  const [emailText, setEmailText] = useState('');
  const [parsing, setParsing] = useState(false);
  const [result, setResult] = useState(null);

  const parse = async () => {
    if (emailText.trim().length < 20) {
      toast.error('Paste the full email body (subject + content)');
      return;
    }
    try {
      setParsing(true);
      const res = await axios.post(
        `${API_URL}/api/admin/partner-revenue/parse-email`,
        { email_text: emailText },
        { withCredentials: true },
      );
      setResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Parse failed');
    } finally {
      setParsing(false);
    }
  };

  const confidenceBadge = (c) => {
    if (c === 'high') return 'bg-[#00FF94]/15 text-[#00FF94] border-[#00FF94]/30';
    if (c === 'medium') return 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30';
    return 'bg-red-500/15 text-red-300 border-red-500/30';
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
      data-testid="parse-email-modal"
    >
      <div
        className="relative w-full max-w-2xl rounded-2xl border border-white/10 bg-neutral-950 shadow-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-white/50 hover:text-white"
          data-testid="parse-email-modal-close"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="p-6 space-y-5">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <MailOpen className="h-5 w-5 text-[#00F0FF]" />
              <h2 className="text-lg font-bold text-white">Parse commission email</h2>
            </div>
            <p className="text-xs text-white/50">
              Paste the full email (subject + body) from any affiliate network — Rewardful, PartnerStack,
              Dub, FirstPromoter, native programs, etc. Claude will extract the partner, amount, and date.
              You still review and click Save.
            </p>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-neutral-400 mb-1.5 block">
              Email content
            </Label>
            <textarea
              value={emailText}
              onChange={(e) => { setEmailText(e.target.value); setResult(null); }}
              placeholder={'From: affiliates@rewardful.com\nSubject: You Earned a Commission\n\nHi Jake — you just earned a $19.80 commission from Emergent...'}
              className="w-full h-40 bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 font-mono leading-relaxed resize-y"
              data-testid="parse-email-textarea"
              autoFocus
            />
            <p className="text-[11px] text-white/40 mt-1">
              {emailText.length.toLocaleString()} characters · max 50,000
            </p>
          </div>

          {result && (
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-3" data-testid="parse-result-preview">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <h3 className="font-bold text-white text-sm">Extracted preview</h3>
                <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded border ${confidenceBadge(result.confidence)}`}>
                  {result.confidence} confidence
                </span>
              </div>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <dt className="text-white/60">Partner</dt>
                <dd className="text-white font-semibold" data-testid="parse-result-partner">
                  {result.partner_slug ? (
                    <>
                      <code className="text-[#00F0FF]">{result.partner_slug}</code>
                      <span className="text-white/50 ml-2 text-xs">({result.partner_name_in_email})</span>
                    </>
                  ) : (
                    <span className="text-red-300">⚠ No match — manual entry needed</span>
                  )}
                </dd>
                <dt className="text-white/60">Commission</dt>
                <dd className="text-[#00FF94] font-bold tabular-nums" data-testid="parse-result-amount">
                  ${result.revenue_usd.toFixed(2)} {result.currency !== 'USD' && <span className="text-white/50 text-xs">({result.currency})</span>}
                </dd>
                <dt className="text-white/60">Conversions</dt>
                <dd className="text-white tabular-nums">{result.conversions}</dd>
                <dt className="text-white/60">Period</dt>
                <dd className="text-white tabular-nums">{result.period_year}-{String(result.period_month).padStart(2, '0')}</dd>
              </dl>
              {result.reasoning && (
                <p className="text-[11px] text-white/50 italic border-t border-white/5 pt-2">
                  {result.reasoning}
                </p>
              )}
              {result.partner_slug_unmatched && (
                <p className="text-[11px] text-yellow-300/80 border-t border-yellow-500/20 pt-2">
                  ⚠ LLM suggested slug <code>{result.partner_slug_unmatched}</code> which isn't in your directory.
                  Add this partner to <code>Affiliate Partners</code> first, or pick the right row manually below.
                </p>
              )}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-1">
            <Button
              variant="outline"
              onClick={onClose}
              className="border-white/20 text-white hover:bg-white/10"
              data-testid="parse-email-cancel-btn"
            >
              Cancel
            </Button>
            {!result ? (
              <Button
                onClick={parse}
                disabled={parsing || emailText.trim().length < 20}
                className="bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-black font-semibold"
                data-testid="parse-email-parse-btn"
              >
                {parsing
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Parsing…</>
                  : <><Sparkles className="w-4 h-4 mr-2" />Parse with Claude</>}
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => setResult(null)}
                  className="border-white/20 text-white hover:bg-white/10"
                  data-testid="parse-email-re-parse-btn"
                >
                  Re-parse
                </Button>
                <Button
                  onClick={() => onApply(result)}
                  disabled={!result.partner_slug}
                  className="bg-[#00FF94] hover:bg-[#00FF94]/90 text-black font-semibold"
                  data-testid="parse-email-apply-btn"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Use these values
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
